import { TOOL_NAMES } from 'js/src/agent/toolNames';

/**
 * The base scene of a voice-driven scene group is not authored by anyone — it is created
 * automatically from the template's default talking-head/voiceover design and carries the voice
 * asset and the group's timing. It shows up in the video state like any other scene (with a
 * `design`, and for talking-head groups with the presenter's `media_1`), so without being told
 * otherwise the model reads it as a scene someone chose and tries to treat it as one.
 *
 * These notes say what it is and how to change what shows: add a scene over the words it should
 * cover. There is one per way a base scene turns up — with a new scene group, left either side of
 * a scene just added, or copied into the half a split broke off — because what the model needs
 * told differs in each. They are appended only when a base scene is actually there to explain:
 * adding a scene cuts the base scene back to the spans either side, and a span of zero length is
 * dropped, so a scene covering the whole thing (a full-transcript audiogram) leaves none at all.
 */

/** Appended when a tool creates a voice-driven scene group, whose base scene comes with it. */
export function newSceneGroupBaseSceneNote(): string {
    return (
        `\n\nThis scene group's base scene is generated automatically from the template's default ` +
        `voice-driven design — nobody picked it, and it fills any stretch of the narration no ` +
        `other scene covers. To show something else, add a scene with ` +
        `${TOOL_NAMES.addVoiceoverScene} covering the words it should appear over: the base scene ` +
        `is cut back to the words either side, so your design replaces it for the words you cover.`
    );
}

/**
 * Appended when a tool leaves *additional* base scenes behind. Adding a scene part-way through a
 * base scene cuts it back to the words either side, and each remaining span stays a base scene —
 * so scenes the model never asked for appear in the video state.
 */
export function addedSceneBaseSceneNote(): string {
    return (
        `\n\nThe words either side of what you added are still on the group's ` +
        `automatically-generated base scene, which now appears in the video state as one scene per ` +
        `remaining span. They are not scenes you created — to change ` +
        `what shows over one, add a scene across its words with ${TOOL_NAMES.addVoiceoverScene}.`
    );
}

/**
 * Appended when splitting a scene group hands the new half a base scene. Nothing was added here:
 * the split copies the original group, so whatever base scene covered the words that moved is now
 * a base scene of the new group. The model didn't create it and shouldn't try to tidy it away.
 */
export function splitBaseSceneNote(newGroupCount = 1): string {
    const carries =
        newGroupCount === 1
            ? 'The new scene group carries a copy'
            : 'Each new scene group carries a copy';
    return (
        `\n\n${carries} of the original group's ` +
        `automatically-generated base scene, covering any of its words no other scene does. It is ` +
        `not a scene you created and doesn't need cleaning up — to change what shows over those ` +
        `words, add a scene across them with ${TOOL_NAMES.addVoiceoverScene}.`
    );
}
